import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Pages
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import AdminPage from "@/pages/AdminPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// API instance with auth
export const api = axios.create({
  baseURL: API,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("member");
      window.location.href = "/";
    }
    return Promise.reject(error);
  }
);

// Auth Provider
const AuthProvider = ({ children }) => {
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const savedMember = localStorage.getItem("member");
    
    if (token && savedMember) {
      setMember(JSON.parse(savedMember));
      // Verify token is still valid
      api.get("/auth/me").then((res) => {
        setMember(res.data);
        localStorage.setItem("member", JSON.stringify(res.data));
      }).catch(() => {
        localStorage.removeItem("token");
        localStorage.removeItem("member");
        setMember(null);
      }).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    localStorage.setItem("token", res.data.access_token);
    localStorage.setItem("member", JSON.stringify(res.data.member));
    setMember(res.data.member);
    return res.data.member;
  };

  const register = async (email, password, displayName) => {
    const res = await api.post("/auth/register", { 
      email, 
      password, 
      display_name: displayName 
    });
    localStorage.setItem("token", res.data.access_token);
    localStorage.setItem("member", JSON.stringify(res.data.member));
    setMember(res.data.member);
    return res.data.member;
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("member");
    setMember(null);
  };

  const refreshMember = async () => {
    const res = await api.get("/auth/me");
    setMember(res.data);
    localStorage.setItem("member", JSON.stringify(res.data));
    return res.data;
  };

  return (
    <AuthContext.Provider value={{ member, login, register, logout, loading, refreshMember }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { member, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505]">
        <div className="text-gold animate-pulse font-cinzel text-xl">Entering the shadows...</div>
      </div>
    );
  }

  if (!member) {
    return <Navigate to="/" replace />;
  }

  if (adminOnly && !member.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LoginPage />} />
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin" 
            element={
              <ProtectedRoute adminOnly>
                <AdminPage />
              </ProtectedRoute>
            } 
          />
        </Routes>
      </BrowserRouter>
      <Toaster 
        position="top-right" 
        toastOptions={{
          style: {
            background: '#0a0a0a',
            border: '1px solid rgba(197, 160, 89, 0.3)',
            color: '#e5c07b',
          },
        }}
      />
    </AuthProvider>
  );
}

export default App;
